// ── SEGNALAZIONI ───────────────────────────────────────────────────
// Un maestro che segnala un problema dal campo non sa dire versione,
// telefono e circolo, e non deve: ce li mettiamo noi. Dice solo cosa è
// successo e quanto lo ferma. Le segnalazioni finiscono nella collection
// `feedback`, che nessun client legge: le legge chi amministra l'app, con
// scripts/listFeedback.js.
//
// I limiti stanno anche in firestore.rules: chi cambia uno dei due cambia
// anche l'altro.

export const FEEDBACK_SEVERITIES = [
	{ value: "blocking", text: "Non riesco a lavorare" },
	{ value: "annoying", text: "Mi dà fastidio" },
	{ value: "idea", text: "È un'idea" },
]

export const FEEDBACK_MAX_TEXT = 2000

// Lo screenshot sta nel documento, come data URI: il progetto non usa
// Firebase Storage, e un documento arriva a un mega. Il lato lungo a 1000
// pixel basta a leggere un'agenda; se anche così pesa troppo, si chiede
// un'immagine più piccola.
export const SCREENSHOT_MAX_SIDE = 1000
export const SCREENSHOT_MAX_CHARS = 600000
const SCREENSHOT_QUALITIES = [0.8, 0.6, 0.4]

export const isFeedbackSeverity = value =>
	FEEDBACK_SEVERITIES.some(severity => severity.value === value)

export const canSendFeedback = ({ text, severity }) =>
	(text ?? "").trim() !== "" && isFeedbackSeverity(severity)

// ── DA DOVE È PARTITO ──────────────────────────────────────────────
// "Segnala un problema" sta nel profilo, quindi la pagina in cui si apre il
// modulo è sempre quella: il problema, di solito, il maestro l'ha visto
// nella pagina di prima. Teniamo a mente l'ultima pagina diversa da quella
// in cui si è adesso. App.js ce le dice tutte, una per volta.
let currentPage = null
let previousPage = null

export const rememberPage = pathname => {
	if (!pathname || pathname === currentPage) return
	previousPage = currentPage
	currentPage = pathname
}

export const visitedPages = () => ({ page: currentPage, previousPage })

// solo per i test
export const forgetPages = () => {
	currentPage = null
	previousPage = null
}

// Il contesto che accompagna la segnalazione. Riceve tutto da fuori — il
// modulo raccoglie dai provider e dal browser — così si prova senza
// montare niente.
export const buildFeedbackContext = ({
	coachID,
	schoolID,
	role,
	page,
	previousPage,
	selectedDate,
	lessonsView,
	appVersion,
	buildCommit,
	device,
	theme,
	themeMode,
	online,
	userAgent,
	screen,
	now = new Date(),
}) => ({
	coachID: coachID ?? "",
	schoolID: schoolID ?? null,
	role: role ?? null,
	page: page ?? null,
	previousPage: previousPage ?? null,
	selectedDate: selectedDate ?? null,
	lessonsView: lessonsView ?? null,
	appVersion: appVersion ?? null,
	buildCommit: buildCommit || null,
	device: device ?? null,
	theme: theme ?? null,
	themeMode: themeMode ?? null,
	online: online !== false,
	userAgent: userAgent ? String(userAgent).slice(0, 300) : null,
	screen: screen ?? null,
	// l'ora del telefono: se la segnalazione parte senza rete, `createdAt`
	// è quando arriva al server, non quando il maestro l'ha scritta
	clientTime: now.toISOString(),
})

// Il documento da salvare, senza `createdAt`: quello lo mette il server.
export const buildFeedback = ({ text, severity, context, screenshot }) => ({
	text: text.trim().slice(0, FEEDBACK_MAX_TEXT),
	severity,
	context,
	...(screenshot ? { screenshot } : {}),
	status: "new",
})

// ── LO SCREENSHOT ──────────────────────────────────────────────────
// Le misure da dare all'immagine perché il lato lungo non superi `maxSide`.
// Un'immagine già piccola resta com'è: ingrandirla pesa e non aggiunge
// niente.
export const fitWithin = (width, height, maxSide = SCREENSHOT_MAX_SIDE) => {
	const scale = Math.min(1, maxSide / Math.max(width, height))
	return { width: Math.round(width * scale), height: Math.round(height * scale) }
}

export class ScreenshotTooLargeError extends Error {
	constructor() {
		super("Questa immagine è troppo pesante: scegline una più piccola")
		this.name = "ScreenshotTooLargeError"
	}
}

const loadImage = file =>
	new Promise((resolve, reject) => {
		const url = URL.createObjectURL(file)
		const image = new Image()
		image.onload = () => {
			URL.revokeObjectURL(url)
			resolve(image)
		}
		image.onerror = () => {
			URL.revokeObjectURL(url)
			reject(new Error("Non riesco ad aprire questa immagine"))
		}
		image.src = url
	})

// Ridimensiona nel browser e ne fa un JPEG, scendendo di qualità finché
// non sta nel documento. Il fondo è bianco: un PNG trasparente in JPEG
// verrebbe nero.
export const screenshotToDataURI = async file => {
	const image = await loadImage(file)
	const { width, height } = fitWithin(image.naturalWidth, image.naturalHeight)

	const canvas = document.createElement("canvas")
	canvas.width = width
	canvas.height = height
	const context = canvas.getContext("2d")
	context.fillStyle = "#ffffff"
	context.fillRect(0, 0, width, height)
	context.drawImage(image, 0, 0, width, height)

	for (const quality of SCREENSHOT_QUALITIES) {
		const dataURI = canvas.toDataURL("image/jpeg", quality)
		// un browser che non sa fare JPEG ripiega sul PNG, che le regole
		// non accettano
		if (!dataURI.startsWith("data:image/jpeg;base64,")) {
			throw new Error("Questo browser non riesce a preparare l'immagine")
		}
		if (dataURI.length <= SCREENSHOT_MAX_CHARS) return dataURI
	}
	throw new ScreenshotTooLargeError()
}
